import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Integration } from '@/constants/integrations'

import { ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { CtaPanel } from '@/components/generals/cta-panel'
import { Reveal } from '@/components/generals/reveal'
import { ArrowLink, ButtonLink } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { IntegrationCard } from '@/components/industries/integration-card'
import {
    FaqList,
    FlowsGrid,
    OutcomesStrip,
    SystemsList
} from '@/components/industries/integration-detail'
import { getIntegration, integrations } from '@/constants/integrations'

interface IntegrationPageProps {
    integration: Integration
    related: Array<Pick<Integration, 'slug' | 'name' | 'short'>>
}

const IntegrationPage: FC<IntegrationPageProps> = ({ integration, related }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={integration.name}
                description={`${integration.tagline} ${integration.intro}`}
            />

            <PageHero
                label="Integrations"
                titleTop={`${integration.name}.`}
                titleBottom={integration.tagline}
                description={integration.intro}
            >
                <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-7'>
                    <ButtonLink href='/contact' size='lg'>
                        Talk to a specialist
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                    </ButtonLink>

                    <ArrowLink href='/industries#integrations'>
                        All integrations
                    </ArrowLink>
                </div>
            </PageHero>

            <SystemsList systems={integration.systems} />
            <FlowsGrid flows={integration.flows} />
            <OutcomesStrip outcomes={integration.outcomes} />
            <FaqList faqs={integration.faqs} />

            <section id='related' aria-labelledby='related-title' className='section-y'>
                <div className='shell'>
                    <SectionHeading
                        id='related-title'
                        eyebrow="Keep exploring"
                        title="Often"
                        accent="paired with."
                        action={(
                            <ArrowLink href='/industries#integrations'>
                                See every integration
                            </ArrowLink>
                        )}
                    />

                    <ul className='mt-12 grid gap-4 md:grid-cols-3 lg:mt-16'>
                        {related.map((item, index) => (
                            <Reveal as='li' key={item.slug} delay={index * 90}>
                                <IntegrationCard integration={item} />
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <CtaPanel />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: integrations.map(item => ({ params: { slug: item.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<IntegrationPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const integration = getIntegration(slug)

    if (!integration) return { notFound: true }

    const index = integrations.findIndex(item => item.slug === slug)
    const related = [1, 2, 3]
        .map(offset => integrations[(index + offset) % integrations.length])
        .map(item => ({ slug: item.slug, name: item.name, short: item.short }))

    return { props: { integration, related } }
}

export default IntegrationPage
